'use client';

import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
  relatedQuestions?: number[];
}

const FAQLandingInteractive: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQ[] = [
    // Probate & Estate Litigation
    {
      id: 1,
      category: 'Probate & Estate Litigation',
      question: 'What is probate and when is it required?',
      answer:
        'Probate is the legal process of validating a will and distributing assets after someone passes away. It\'s required when the deceased owned assets solely in their name without beneficiary designations. Our experienced probate attorneys guide families through this process with compassion and efficiency, ensuring proper estate administration and resolution of any disputes.',
      relatedQuestions: [2, 3],
    },
    {
      id: 2,
      category: 'Probate & Estate Litigation',
      question: 'How long does the probate process typically take?',
      answer:
        'The probate timeline varies based on estate complexity, but typically ranges from 6 months to 2 years. Factors affecting duration include estate size, will contests, creditor claims, and court schedules. Our team works diligently to expedite the process while protecting your interests and ensuring all legal requirements are met.',
      relatedQuestions: [1, 4],
    },
    {
      id: 3,
      category: 'Probate & Estate Litigation',
      question: 'Can I contest a will if I believe it\'s unfair?',
      answer:
        'Yes, you can contest a will if you have legal standing and valid grounds such as lack of testamentary capacity, undue influence, fraud, or improper execution. Our will contest attorneys have extensive experience challenging invalid wills and protecting rightful beneficiaries. We evaluate your case thoroughly and pursue the most effective legal strategy.',
      relatedQuestions: [1, 5],
    },
    {
      id: 4,
      category: 'Probate & Estate Litigation',
      question: 'What are the duties of an estate executor?',
      answer:
        'An executor must inventory assets, pay debts and taxes, manage estate property, and distribute assets to beneficiaries according to the will. This fiduciary role requires careful attention to legal obligations. We advise executors on their responsibilities and represent beneficiaries when executors breach their duties.',
      relatedQuestions: [2, 5],
    },
    {
      id: 5,
      category: 'Probate & Estate Litigation',
      question: 'What happens if someone dies without a will?',
      answer:
        'When someone dies intestate (without a will), state intestacy laws determine asset distribution, typically prioritizing spouses and children. The court appoints an administrator to handle the estate. Our attorneys guide families through intestate proceedings and help resolve disputes that may arise during this process.',
      relatedQuestions: [1, 4],
    },

    // Personal Injury Law
    {
      id: 6,
      category: 'Personal Injury Law',
      question: 'What compensation can I receive in a personal injury case?',
      answer:
        'Personal injury compensation includes economic damages (medical bills, lost wages, future medical care) and non-economic damages (pain and suffering, emotional distress, loss of enjoyment of life). In cases of gross negligence, punitive damages may apply. Our attorneys have recovered millions for injured clients by thoroughly documenting all damages and aggressively negotiating with insurance companies.',
      relatedQuestions: [7, 9],
    },
    {
      id: 7,
      category: 'Personal Injury Law',
      question: 'How long do I have to file a personal injury lawsuit?',
      answer:
        'Statutes of limitations vary by state and injury type, typically ranging from 1-3 years from the injury date or discovery. Medical malpractice and wrongful death claims may have different deadlines. Acting quickly preserves evidence and witness testimony. Contact us immediately for a free consultation to protect your rights.',
      relatedQuestions: [6, 8],
    },
    {
      id: 8,
      category: 'Personal Injury Law',
      question: 'What should I do immediately after an accident?',
      answer:
        'Seek medical attention immediately, even if injuries seem minor. Document the scene with photos and witness information. File a police report if applicable. Avoid admitting fault or giving recorded statements to insurance companies before consulting an attorney. Our team provides guidance on protecting your claim from the start.',
      relatedQuestions: [7, 9],
    },
    {
      id: 9,
      category: 'Personal Injury Law',
      question: 'Do I need a lawyer for my personal injury case?',
      answer:
        'While not legally required, attorney representation significantly increases settlement values and success rates. Insurance companies have experienced lawyers protecting their interests—you deserve the same. We handle all legal complexities, negotiate aggressively, and work on contingency (no fee unless we win), removing financial barriers to quality representation.',
      relatedQuestions: [6, 10],
    },
    {
      id: 10,
      category: 'Personal Injury Law',
      question: 'What if I was partially at fault for my accident?',
      answer:
        'Many states follow comparative negligence rules, allowing recovery even if you share some fault, though your compensation may be reduced proportionally. Our attorneys carefully investigate accident circumstances, challenge unfair fault attributions, and maximize your recovery regardless of shared responsibility.',
      relatedQuestions: [9, 11],
    },
    {
      id: 11,
      category: 'Personal Injury Law',
      question: 'How are car accident settlements calculated?',
      answer:
        'Settlements consider medical expenses, lost income, property damage, pain and suffering, and future care needs. We analyze accident severity, injury permanence, liability clarity, and insurance policy limits. Our experienced negotiators leverage decades of case results to secure maximum compensation for our clients.',
      relatedQuestions: [6, 10],
    },

    // Insurance Law
    {
      id: 12,
      category: 'Insurance Law',
      question: 'My insurance claim was denied—what are my options?',
      answer:
        'You have the right to appeal the denial through your insurer\'s internal review process. If that fails, you can file a complaint with your state insurance department or pursue bad faith litigation. Our insurance law attorneys review denial reasons, identify policy violations, and fight for the coverage you paid for.',
      relatedQuestions: [13, 14],
    },
    {
      id: 13,
      category: 'Insurance Law',
      question: 'What is insurance bad faith and how do I prove it?',
      answer:
        'Insurance bad faith occurs when insurers unreasonably deny claims, delay payments, fail to investigate properly, or misrepresent policy terms. Proving bad faith requires documenting communication, comparing similar approved claims, and demonstrating the insurer violated its duty of good faith. We have successfully held insurers accountable through bad faith litigation.',
      relatedQuestions: [12, 15],
    },
    {
      id: 14,
      category: 'Insurance Law',
      question: 'Can I sue my insurance company?',
      answer:
        'Yes, you can sue for breach of contract if the insurer fails to honor policy terms, or for bad faith if they handle your claim unreasonably. Insurance litigation is complex, requiring deep understanding of policy language and insurance law. Our attorneys have decades of experience challenging unfair insurance practices.',
      relatedQuestions: [12, 13],
    },
    {
      id: 15,
      category: 'Insurance Law',
      question: 'What should I do if my claim is being delayed?',
      answer:
        'Document all delays, maintain communication records, and send written requests for status updates. Insurers must handle claims promptly under state regulations. Unreasonable delays may constitute bad faith. Contact us if your claim has been pending excessively—we can intervene and accelerate the process.',
      relatedQuestions: [13, 16],
    },
    {
      id: 16,
      category: 'Insurance Law',
      question: 'How long does an insurance company have to respond to a claim?',
      answer:
        'State laws typically require insurers to acknowledge claims within 10-30 days and make coverage decisions within 30-90 days. Specific timeframes vary by state and claim type. Failure to meet these deadlines may support a bad faith claim. We hold insurers accountable to statutory requirements.',
      relatedQuestions: [15, 12],
    },

    // Contracts & Business Agreements
    {
      id: 17,
      category: 'Contracts & Business Agreements',
      question: 'What makes a contract legally enforceable?',
      answer:
        'An enforceable contract requires offer, acceptance, consideration (something of value exchanged), mutual intent to be bound, and legal capacity. The terms must be sufficiently definite and the purpose legal. We draft airtight contracts that protect your business interests and minimize dispute risks.',
      relatedQuestions: [18, 20],
    },
    {
      id: 18,
      category: 'Contracts & Business Agreements',
      question: 'What should I do if someone breaches a contract with me?',
      answer:
        'Document the breach thoroughly, review contract terms regarding remedies and dispute resolution, and attempt good-faith negotiation. If negotiation fails, formal demand letters, mediation, or litigation may be necessary. Our business litigation attorneys protect your contractual rights and pursue damages for breaches.',
      relatedQuestions: [17, 19],
    },
    {
      id: 19,
      category: 'Contracts & Business Agreements',
      question: 'Do I need a lawyer to review my business contract?',
      answer:
        'While not legally required, attorney review is highly recommended before signing significant business agreements. We identify unfavorable terms, ambiguous language, and missing protections that could cost you later. Our contract review prevents disputes and ensures agreements align with your business objectives.',
      relatedQuestions: [17, 20],
    },
    {
      id: 20,
      category: 'Contracts & Business Agreements',
      question: 'What damages can I recover for breach of contract?',
      answer:
        'Contract damages typically include direct losses (compensatory damages), consequential damages if foreseeable, and sometimes attorney fees if specified in the contract. Specific performance may be available for unique goods or property. We maximize recovery by thoroughly documenting all damages flowing from the breach.',
      relatedQuestions: [18, 17],
    },

    // General Legal Process
    {
      id: 21,
      category: 'General Legal Process',
      question: 'How much does it cost to hire a lawyer?',
      answer:
        'Our fee structure varies by case type. Personal injury cases are handled on contingency (we only get paid if you win, typically 33-40% of recovery). Other matters may involve hourly rates, flat fees, or hybrid arrangements. We offer transparent pricing, flexible payment plans, and free initial consultations to make quality legal representation accessible.',
      relatedQuestions: [22, 23],
    },
    {
      id: 22,
      category: 'General Legal Process',
      question: 'How long will my case take?',
      answer:
        'Case duration depends on complexity, court schedules, and whether settlement is reached. Simple matters may resolve in weeks, while complex litigation can take 1-2 years or longer. We provide realistic timelines during consultation and work efficiently to resolve your case as quickly as possible without compromising results.',
      relatedQuestions: [21, 24],
    },
    {
      id: 23,
      category: 'General Legal Process',
      question: 'What happens during a free consultation?',
      answer:
        'During your free consultation, we discuss your legal issue, review relevant documents, explain your options and potential outcomes, answer your questions, and outline our strategy. There\'s no obligation—this meeting helps you make informed decisions about representation. We respect your time and provide honest assessments.',
      relatedQuestions: [21, 24],
    },
    {
      id: 24,
      category: 'General Legal Process',
      question: 'How do I prepare for meeting with an attorney?',
      answer:
        'Gather relevant documents (contracts, medical records, correspondence, police reports), prepare a timeline of events, list key witnesses, write down questions, and bring identification. The more organized your information, the more productive our consultation. Don\'t worry if you don\'t have everything—we\'ll guide you through what\'s needed.',
      relatedQuestions: [23, 22],
    },
  ];

  const categories = [
    'All',
    'Probate & Estate Litigation',
    'Personal Injury Law',
    'Insurance Law',
    'Contracts & Business Agreements',
    'General Legal Process',
  ];

  const filteredFAQs = useMemo(() => {
    let filtered = selectedCategory === 'All' ? faqs : faqs.filter((faq) => faq.category === selectedCategory);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (faq) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [selectedCategory, searchQuery, faqs]);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const getRelatedQuestions = (faqId: number): FAQ[] => {
    const faq = faqs.find((f) => f.id === faqId);
    if (!faq?.relatedQuestions) return [];
    return faqs.filter((f) => faq.relatedQuestions?.includes(f.id)).slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section with Search */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary via-navy-dark to-primary overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/assets/images/about_us_hero_legal_background.png')] bg-cover bg-center" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <Icon name="QuestionMarkCircleIcon" size={64} className="text-accent" />
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">
              Legal Questions Answered
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Find expert answers to your legal questions. Search our comprehensive FAQ or browse by practice area to
              get the information you need.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Icon
                  name="MagnifyingGlassIcon"
                  size={24}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for legal topics, questions, or keywords..."
                  className="w-full pl-14 pr-4 py-4 rounded-xl border-2 border-white/20 bg-white/95 backdrop-blur-sm text-primary placeholder-text-secondary focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all duration-300"
                />
              </div>
              {searchQuery && (
                <p className="mt-3 text-white/80 text-sm">
                  Found {filteredFAQs.length} result{filteredFAQs.length !== 1 ? 's' : ''} for "{searchQuery}"
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-surface border-b border-border sticky top-20 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setSearchQuery('');
                  setOpenIndex(null);
                }}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-accent text-primary shadow-elevation-sm scale-105'
                    : 'bg-white text-text-secondary border border-border hover:border-accent hover:text-accent hover:scale-102'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={64} className="text-text-secondary mx-auto mb-4 opacity-50" />
              <h3 className="text-2xl font-serif font-bold text-primary mb-3">No Results Found</h3>
              <p className="text-text-secondary mb-6">
                We couldn't find any FAQs matching your search. Try different keywords or browse by category.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All');
                }}
                className="px-6 py-3 bg-accent hover:bg-gold-dark text-primary font-semibold rounded-lg transition-all duration-300"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredFAQs.map((faq, index) => (
                <div key={faq.id} className="bg-white rounded-xl shadow-subtle hover:shadow-elevation-sm transition-all duration-300">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-start justify-between p-6 text-left hover:bg-surface/50 transition-colors duration-300 rounded-xl"
                  >
                    <div className="flex-1 pr-4">
                      <span className="inline-block px-3 py-1 text-xs font-semibold text-accent bg-accent/10 rounded-full mb-3">
                        {faq.category}
                      </span>
                      <h3 className="text-xl font-semibold text-primary mb-1">{faq.question}</h3>
                    </div>
                    <Icon
                      name="ChevronDownIcon"
                      size={28}
                      className={`text-accent flex-shrink-0 transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6">
                      <div className="pt-4 border-t border-border">
                        <p className="text-text-secondary leading-relaxed mb-6">{faq.answer}</p>

                        {/* Related Questions */}
                        {getRelatedQuestions(faq.id).length > 0 && (
                          <div className="mt-6 p-4 bg-surface rounded-lg">
                            <h4 className="text-sm font-semibold text-primary mb-3 flex items-center">
                              <Icon name="LinkIcon" size={16} className="mr-2 text-accent" />
                              Related Questions
                            </h4>
                            <div className="space-y-2">
                              {getRelatedQuestions(faq.id).map((related) => (
                                <button
                                  key={related.id}
                                  onClick={() => {
                                    const relatedIndex = filteredFAQs.findIndex((f) => f.id === related.id);
                                    if (relatedIndex !== -1) {
                                      setOpenIndex(relatedIndex);
                                      setTimeout(() => {
                                        document
                                          .querySelectorAll('.bg-white.rounded-xl')
                                          [relatedIndex]?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'center',
                                          });
                                      }, 100);
                                    }
                                  }}
                                  className="block w-full text-left px-3 py-2 text-sm text-primary hover:text-accent hover:bg-white rounded transition-all duration-200"
                                >
                                  → {related.question}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Consultation CTA within answer */}
                        <div className="mt-6 p-4 bg-gradient-to-r from-accent/10 to-gold-light/10 rounded-lg border border-accent/20">
                          <p className="text-sm text-primary mb-3">
                            <strong>Need personalized guidance?</strong> Our attorneys can provide specific advice for
                            your situation.
                          </p>
                          <a
                            href="/contact"
                            className="inline-flex items-center text-sm font-semibold text-accent hover:text-gold-dark transition-colors duration-300"
                          >
                            Schedule a Free Consultation
                            <Icon name="ArrowRightIcon" size={16} className="ml-2" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-gradient-to-br from-primary via-navy-dark to-primary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Icon name="ChatBubbleLeftRightIcon" size={64} className="text-accent mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
            Can't find the answer you're looking for? Our experienced attorneys are here to provide personalized
            guidance and answer your specific legal questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent hover:bg-gold-dark text-primary font-semibold rounded-lg shadow-elevation-sm hover:shadow-elevation-md transition-all duration-300 hover:scale-105"
            >
              Schedule Free Consultation
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </a>
            <a
              href="tel:+1234567890"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border-2 border-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-105"
            >
              <Icon name="PhoneIcon" size={20} className="mr-2" />
              Call Now: (123) 456-7890
            </a>
          </div>

          {/* Trust Signals */}
          <div className="mt-12 pt-12 border-t border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">25+</div>
                <div className="text-white/80">Years Combined Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">1000+</div>
                <div className="text-white/80">Cases Successfully Resolved</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">98%</div>
                <div className="text-white/80">Client Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQLandingInteractive;